import { MouseEvent, useCallback, useEffect, useRef, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    FormControl,
    FormHelperText,
    IconButton,
    List,
    ListItem,
    ListItemText,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { useStyles } from "./ShiftsInput.styles";
import { IShiftsInput } from "./ShiftsInputInterfaces";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { IShift as IShiftMillEntities } from "entities/Mills/MillsEntityInterfaces";
import { IShift } from "../MillSettingsStepInterfaces";
import { areShiftDaysIdsEqual, checkShiftOverlappingAtShiftEditing } from "utilities/utilities";
import { FormikErrors } from "formik";
import moment from "moment";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";
import { ShiftDeletionForm, IShiftDeletionData, ModalShiftDeletion } from "./components";

const ShiftsInput = ({
    values,
    setFieldValue,
    handleChange,
    dayArray,
    errors,
    touched,
    editShiftsField,
    setEditShiftsField,
    productionLinesShifts,
}: IShiftsInput) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();

    const [expandArray, setExpandArray] = useState<boolean[]>(values.shifts.map(() => true));
    const [sameShiftNameError, setSameShiftNameError] = useState<boolean>(false);
    const [sameShiftNameEditionError, setSameShiftNameEditionError] = useState<boolean>(false);

    //this state stores the overlapping information for all the shifts after each change
    const [overlappingInformation, setOverlappingInformation] = useState<
        {
            singleShiftsArray: number[];
            overlap: boolean;
            shiftHoursByShiftId: {
                [key: number]: string[];
            }[];
        }[]
    >([]);

    // these states store the shifts ids and conflicting times
    const [conflictingShifts, setConflictingShifts] = useState<{ [key: number]: number[] }[]>([]);
    const [conflictingTimes, setConflictingTimes] = useState<
        {
            shift_id: number;
            overlapping_time: string;
        }[]
    >([]);
    const [shiftsWithOverlappingErrorIds, setShiftsWithOverlappingErrorIds] = useState<number[]>([]);

    const [editedShiftsIds, setEditedShiftsIds] = useState<number[]>([]);

    // this state stores the production lines with edited version of each of their shifts
    const [productionLineWithEditedShifts, setProductionLineWithEditedShifts] = useState<
        {
            production_line: {
                production_line_id: number;
                name: string;
            };
            shift: IShiftMillEntities;
        }[][]
    >(productionLinesShifts);
    const [openShiftDeletionModal, setOpenShiftDeletionModal] = useState<boolean>(false);
    const [shiftDeletionData, setShiftDeletionData] = useState<IShiftDeletionData | undefined>(undefined);

    // We store initial values in the ref on mount
    const originalShiftValues = useRef<IShiftMillEntities[]>([]);
    if (!originalShiftValues.current.length) {
        originalShiftValues.current = values.shifts.map((shift) => ({
            name: shift?.name,
            shift_historic: [
                {
                    end_time: shift.shift_historic?.end,
                    shift_days: shift.shift_historic?.days?.map((day) => ({
                        day_id: day,
                        is_enabled: true,
                        day: {
                            name: dayArray.find((d) => d.day_id === day)
                                ? translateDefaultEntity(
                                      i18n.language,
                                      dayArray.find((d) => d.day_id === day),
                                      "name",
                                      DefaultTranslationFieldsInTable.DAY,
                                  )
                                : "",
                        },
                    })),
                    shift_historic_id: shift.shift_historic?.shift_id,
                    start_time: shift.shift_historic?.start,
                    effective_at: shift.shift_historic?.effective_at,
                },
            ],
            shift_id: shift.shift_id,
        }));
    }

    const areShiftsDifferent = useCallback((shift1: IShiftMillEntities, shift2: IShiftMillEntities) => {
        return (
            shift1.shift_historic[0]?.start_time !== shift2.shift_historic[0]?.start_time ||
            shift1.shift_historic[0]?.end_time !== shift2.shift_historic[0]?.end_time ||
            !areShiftDaysIdsEqual(
                shift1.shift_historic[0]?.shift_days &&
                    shift1.shift_historic[0]?.shift_days
                        .filter((shift_day) => shift_day.is_enabled)
                        .map((shiftDay) => shiftDay.day_id)
                        .sort(),
                shift2.shift_historic[0]?.shift_days &&
                    shift2.shift_historic[0]?.shift_days
                        .filter((shift_day) => shift_day.is_enabled)
                        .map((shiftDay) => shiftDay.day_id)
                        .sort(),
            )
        );
    }, []);

    //we check if any shift related or not to a production line has changed from its stored value in the DB so as to enable or disable the set effective date feature
    useEffect(() => {
        values.shifts.forEach((shift) => {
            const originalShift = originalShiftValues.current.find(
                (originalShift) => originalShift.shift_id === shift.shift_id,
            );

            if (originalShift) {
                const editedShiftToType = {
                    name: shift?.name,
                    shift_historic: [
                        {
                            end_time: shift.shift_historic?.end,
                            shift_days: shift.shift_historic?.days?.map((day) => {
                                return {
                                    day_id: day,
                                    is_enabled: true,
                                    day: {
                                        name: dayArray.find((d) => d.day_id === day)
                                            ? translateDefaultEntity(
                                                  i18n.language,
                                                  dayArray.find((d) => d.day_id === day),
                                                  "name",
                                                  DefaultTranslationFieldsInTable.DAY,
                                              )
                                            : "",
                                    },
                                };
                            }),
                            shift_historic_id: shift.shift_historic?.shift_id,
                            start_time: shift.shift_historic?.start,
                            effective_at: shift.shift_historic?.effective_at,
                        },
                    ],
                    shift_id: shift.shift_id,
                };

                const differentShifts = areShiftsDifferent(editedShiftToType, originalShift);

                if (differentShifts) {
                    setEditedShiftsIds((prevArray) => [...prevArray, shift.shift_id as number]);
                    shift.shift_historic.effective_at = moment().add(1, "day").startOf("day").toDate();
                } else {
                    setEditedShiftsIds((prevArray) =>
                        prevArray.filter((number) => number !== (shift.shift_id as number)),
                    );
                    shift.shift_historic.effective_at = originalShift.shift_historic[0]?.effective_at as Date;
                }
            }
        });
    }, [values.shifts, areShiftsDifferent, originalShiftValues, dayArray, i18n.language]);

    // we evaluate the overlapping shifts information, set shiftsWithOverlappingErrorIds and gather the shift id and overlapping time for each shift that we're going to highlight as error
    useEffect(() => {
        const shiftsWithOverlappingErrorsIds = overlappingInformation.flatMap((item) =>
            item.singleShiftsArray.map((id) => id),
        );
        setShiftsWithOverlappingErrorIds(shiftsWithOverlappingErrorsIds);

        const conflictingTimes: {
            shift_id: number;
            overlapping_time: string;
        }[] = [];

        const conflictingShifts: { [key: number]: number[] }[] = [];

        //we calculate what start_time or end_time overlaps with other shift to highlight them in red
        overlappingInformation.forEach((item) => {
            for (let i = 0; i < item.shiftHoursByShiftId.length - 1; i++) {
                const currentShift = item.shiftHoursByShiftId[i];
                const currentShiftId = Number(Object.keys(currentShift)[0]);
                const [currentStartTime, currentEndTime] = currentShift[currentShiftId];

                for (let j = i + 1; j < item.shiftHoursByShiftId.length; j++) {
                    const nextShift = item.shiftHoursByShiftId[j];
                    const nextShiftId = Number(Object.keys(nextShift)[0]);
                    const [nextStartTime, nextEndTime] = nextShift[nextShiftId];

                    if (
                        (currentStartTime < nextEndTime && currentEndTime > nextStartTime) ||
                        (nextStartTime < currentEndTime && nextEndTime > currentStartTime) ||
                        currentStartTime > currentEndTime ||
                        nextStartTime > nextEndTime
                    ) {
                        const currentShift = {
                            shift_id: currentShiftId,
                            overlapping_time:
                                currentStartTime > nextStartTime || currentStartTime > nextEndTime
                                    ? currentStartTime
                                    : currentEndTime,
                        };

                        const nextShift = {
                            shift_id: nextShiftId,
                            overlapping_time:
                                nextStartTime > currentStartTime || nextStartTime > currentEndTime
                                    ? nextStartTime
                                    : nextEndTime,
                        };

                        conflictingShifts.push({ [currentShiftId]: [currentShiftId, nextShiftId] });
                        conflictingShifts.push({ [nextShiftId]: [nextShiftId, currentShiftId] });

                        conflictingTimes.push(currentShift, nextShift);
                    }
                }
            }
        });
        setConflictingShifts(conflictingShifts);
        setConflictingTimes(conflictingTimes);
    }, [overlappingInformation]);

    const checkShiftOverlapping = useCallback(
        (newShift: IShift) => {
            //we format the new shift coming from values to fit the shift we're going to be comparing it with
            const newShiftToShiftType = {
                name: newShift?.name,
                shift_historic: [
                    {
                        end_time: newShift.shift_historic?.end,
                        shift_days: newShift.shift_historic?.days.map((day) => ({
                            day_id: day,
                            is_enabled: true,
                            day: {
                                name: dayArray.find((d) => d.day_id === day)
                                    ? translateDefaultEntity(
                                          i18n.language,
                                          dayArray.find((d) => d.day_id === day),
                                          "name",
                                          DefaultTranslationFieldsInTable.DAY,
                                      )
                                    : "",
                            },
                        })),
                        shift_historic_id: newShift.shift_historic?.shift_id,
                        start_time: newShift.shift_historic?.start,
                        effective_at: newShift.shift_historic?.effective_at,
                    },
                ],
                shift_id: newShift.shift_id,
            };

            //we modify each production line's shift with the updated values entered in the form, so that if the user modifies more than one before submitted, we check for overlapping in these new values
            const overlappingInformationArray: {
                singleShiftsArray: number[];
                overlap: boolean;
                shiftHoursByShiftId: {
                    [key: number]: string[];
                }[];
            }[] = [];
            if (productionLineWithEditedShifts && productionLineWithEditedShifts.length > 0) {
                productionLineWithEditedShifts.forEach((productionLineShiftGroup) => {
                    const index = productionLineShiftGroup?.findIndex(
                        (existingShift) => existingShift.shift.shift_id === newShiftToShiftType.shift_id,
                    );

                    if (index !== -1) {
                        const differentShifts = areShiftsDifferent(
                            productionLineShiftGroup[index].shift,
                            newShiftToShiftType,
                        );

                        if (differentShifts) {
                            setEditedShiftsIds((prevArray) => [...prevArray, newShiftToShiftType.shift_id as number]);
                            productionLineShiftGroup[index].shift = newShiftToShiftType;

                            setProductionLineWithEditedShifts(productionLineWithEditedShifts);
                        }
                    }

                    //we get all the shifts for production line
                    const shifts = productionLineShiftGroup.map((item) => item.shift);

                    //we check for overlapping
                    const overlappingData = checkShiftOverlappingAtShiftEditing(shifts);

                    if (overlappingData.overlap) {
                        overlappingInformationArray.push(overlappingData);
                    }
                });
            }
            setOverlappingInformation(overlappingInformationArray);
        },
        [productionLineWithEditedShifts, dayArray, i18n.language, areShiftsDifferent],
    );

    const handleShiftInputChange = useCallback(() => {
        if (Boolean(values.shiftInput?.trim())) {
            const duplicatedValues = values.shifts?.findIndex((shift) => {
                return (
                    !Boolean(shift.deleted_at) &&
                    !Boolean(shift.disabled_at) &&
                    shift?.name.toLowerCase() === values.shiftInput.trim().toLowerCase()
                );
            });

            if (duplicatedValues !== -1) {
                setSameShiftNameError(true);
                return;
            } else {
                setSameShiftNameError(false);
            }

            setFieldValue("shifts", [
                {
                    name: values.shiftInput,
                    shift_historic: {
                        start: "09:00:00",
                        end: "18:00:00",
                        days: [1],
                        future_effective_at: moment.utc().startOf("day").toDate(),
                    },
                    created_at: new Date().toISOString(),
                    production_line_shifts_changes: [],
                },
                ...values.shifts,
            ]);

            setExpandArray((prevState) => {
                return [...prevState, true];
            });

            setFieldValue("shiftInput", "");

            setFieldValue("allShiftsSoftDeleted", false);
        }
    }, [values.shifts, values.shiftInput, setFieldValue]);

    const handleShiftInputEdition = useCallback(
        (index: number) => {
            if (Boolean(values.shiftEditInput?.trim())) {
                const duplicatedValues = values.shifts?.findIndex((shift) => {
                    return (
                        !Boolean(shift.deleted_at) &&
                        !Boolean(shift.disabled_at) &&
                        shift?.name.toLowerCase() === values.shiftEditInput.trim().toLowerCase()
                    );
                });

                if (duplicatedValues !== -1 && duplicatedValues !== index) {
                    setSameShiftNameEditionError(true);
                    return;
                } else {
                    setSameShiftNameEditionError(false);
                }

                const shifts = [...values.shifts];
                shifts[index] = {
                    ...shifts[index],
                    name: values.shiftEditInput,
                };

                setFieldValue("shifts", shifts);
                setEditShiftsField(null);
            }
        },
        [setEditShiftsField, setFieldValue, values.shiftEditInput, values.shifts],
    );

    const handleShiftInputEditionCancel = useCallback(() => setEditShiftsField(null), [setEditShiftsField]);

    const handleShiftEditionInit = useCallback(
        (shift: IShift, index: number) => {
            setFieldValue("shiftEditInput", shift?.name);
            setEditShiftsField(index);
        },
        [setFieldValue, setEditShiftsField],
    );

    const handleShiftDayChange = useCallback(
        (index: number, newDays: number[]) => {
            const newShift = { ...values.shifts[index] };
            newShift.shift_historic.days = newDays;
            checkShiftOverlapping(newShift);
            setFieldValue("shifts", [...values.shifts.slice(0, index), newShift, ...values.shifts.slice(index + 1)]);
        },
        [setFieldValue, values.shifts, checkShiftOverlapping],
    );

    const handleShiftDeletion = useCallback(
        (event: MouseEvent, index: number, shift: IShift) => {
            event?.stopPropagation();
            const effectiveDate = moment.utc().add(1, "day").startOf("day").toDate();
            const deletedShift = { ...values.shifts[index] };
            deletedShift.disabled_at = effectiveDate;
            if (shift?.shift_id) {
                deletedShift.production_line_shifts_changes = shift.production_lines.map((pl) => ({
                    production_line_id: pl.production_line_id,
                    production_line_shift_id: pl.production_line_shift_id,
                    disabled_at: effectiveDate,
                    name: pl.name,
                }));
            }
            const changedShifts = [...values.shifts.slice(0, index), deletedShift, ...values.shifts.slice(index + 1)];
            const allShiftsSoftDeleted =
                changedShifts.filter((changedShift, indexFilter) =>
                    changedShift.shift_id
                        ? !changedShift.deleted_at || !changedShift.disabled_at
                        : indexFilter !== index,
                ).length === 0;

            if (allShiftsSoftDeleted) setFieldValue("allShiftsSoftDeleted", true);

            if (shift.shift_id) {
                setFieldValue("shifts", changedShifts);
            } else {
                setFieldValue(
                    "shifts",
                    values.shifts.filter((_, indexFilter) => indexFilter !== index),
                );
            }
        },
        [setFieldValue, values.shifts],
    );

    const handleShiftDeletionModal = useCallback(
        (event: MouseEvent, index: number, shift: IShift, deletionData: IShiftDeletionData["deletionData"]) => {
            event?.stopPropagation();
            setShiftDeletionData({ index, shift, deletionData });
            setOpenShiftDeletionModal(true);
        },
        [],
    );

    const handleCloseShiftDeletionModal = () => {
        setOpenShiftDeletionModal(false);
        setShiftDeletionData(undefined);
    };

    const handleEffectiveAtChange = useCallback(
        (index: number, newEffectiveAt: dayjs.Dayjs | null) => {
            const newShift = { ...values.shifts[index] };
            newShift.shift_historic.future_effective_at = newEffectiveAt!.toDate();
            setFieldValue("shifts", [...values.shifts.slice(0, index), newShift, ...values.shifts.slice(index + 1)]);
        },
        [setFieldValue, values.shifts],
    );

    const handleTimeStartChange = useCallback(
        (newValue: dayjs.Dayjs | null, index: number) => {
            const newShift = { ...values.shifts[index] };
            newShift.shift_historic.start = newValue?.format("HH:mm:ss") || "09:00:00";
            checkShiftOverlapping(newShift);
            setFieldValue("shifts", [...values.shifts.slice(0, index), newShift, ...values.shifts.slice(index + 1)]);
        },
        [values.shifts, setFieldValue, checkShiftOverlapping],
    );

    const handleTimeEndChange = useCallback(
        (newValue: dayjs.Dayjs | null, index: number) => {
            const newShift = { ...values.shifts[index] };
            newShift.shift_historic.end = newValue?.format("HH:mm:ss") || "18:00:00";
            checkShiftOverlapping(newShift);
            setFieldValue("shifts", [...values.shifts.slice(0, index), newShift, ...values.shifts.slice(index + 1)]);
        },
        [values.shifts, setFieldValue, checkShiftOverlapping],
    );

    //we need unique keys for all the mappings, as some ids could be repeated and cause react errors in the console
    let uniqueKeyCounter = 0;
    const generateUniqueKey = () => {
        return `uniqueKey_${uniqueKeyCounter++}`;
    };

    return (
        <>
            <div className={classes.formSubtitle}>{t("mill_setup.shifts.title")}</div>
            <TextField
                fullWidth
                id="shift-input"
                name="shiftInput"
                type="text"
                variant="outlined"
                className={classes.textField}
                label={t("mill_setup.shifts.label")}
                value={values.shiftInput}
                error={
                    sameShiftNameError
                        ? sameShiftNameError
                        : !!errors.shifts &&
                          !!touched.shifts &&
                          !values.shifts.filter((shift) => !shift.deleted_at || !shift.disabled_at).length
                }
                helperText={
                    <span className={classes.helperText}>
                        <span>
                            {sameShiftNameError
                                ? t("mill_setup.error_message.existing_shift_name")
                                : !!errors.shifts &&
                                  !!touched.shifts &&
                                  !values.shifts.filter((shift) => !shift.deleted_at || !shift.disabled_at).length &&
                                  t(t(errors.shifts.toString()))}
                        </span>
                        <span className={classes.effectiveAtInfoText}>{t("mill_setup.shifts.effective_at_info")}</span>
                    </span>
                }
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        event.preventDefault();
                        handleShiftInputChange();
                    }
                }}
                InputProps={{
                    endAdornment: (
                        <IconButton onClick={handleShiftInputChange}>
                            <AddIcon />
                        </IconButton>
                    ),
                }}
                onChange={handleChange}
            />
            <div className={classes.accordionGroup}>
                {values.shifts.map((shift, index) => {
                    const uniqueShifts: number[] = [];
                    return (
                        <Accordion
                            style={{ ...((shift.deleted_at || shift.disabled_at) && { display: "none" }) }}
                            key={generateUniqueKey()}
                            expanded={expandArray[index]}
                            onChange={(event, expanded) =>
                                setExpandArray((prevState) => {
                                    return prevState.map((shiftExpand, indexExpand) =>
                                        indexExpand === index ? expanded : shiftExpand,
                                    );
                                })
                            }
                            elevation={0}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                className={classes.accordionSummary}
                                aria-controls={`panel${index}-content`}
                                id={`panel${index}-header`}
                            >
                                {editShiftsField !== index && <Typography>{shift?.name}</Typography>}

                                {editShiftsField === index && (
                                    <TextField
                                        fullWidth
                                        label={t("mill_setup.shifts.edit_name")}
                                        variant="outlined"
                                        autoFocus={true}
                                        name="shiftEditInput"
                                        onChange={handleChange}
                                        error={sameShiftNameEditionError}
                                        helperText={
                                            sameShiftNameEditionError &&
                                            t("mill_setup.error_message.existing_shift_name")
                                        }
                                        value={values.shiftEditInput}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                                event.preventDefault();
                                                handleShiftInputEdition(index);
                                            }
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <>
                                                    <IconButton
                                                        onClick={(event) => {
                                                            handleShiftInputEdition(index);
                                                            event.stopPropagation();
                                                        }}
                                                    >
                                                        <CheckIcon />
                                                    </IconButton>
                                                    <IconButton onClick={handleShiftInputEditionCancel}>
                                                        <CloseIcon />
                                                    </IconButton>
                                                </>
                                            ),
                                        }}
                                    />
                                )}

                                {editShiftsField !== index && (
                                    <div className={classes.iconsContainer}>
                                        <FormControl className={classes.effectiveDateContainer}>
                                            <DatePicker
                                                value={
                                                    shift.shift_historic?.future_effective_at
                                                        ? dayjs(shift.shift_historic.future_effective_at).startOf("day")
                                                        : shift.shift_historic.effective_at
                                                        ? dayjs(shift.shift_historic.effective_at).startOf("day")
                                                        : dayjs().add(1, "day").startOf("day")
                                                }
                                                disabled={
                                                    shift.shift_id
                                                        ? !editedShiftsIds.includes(shift.shift_id as number)
                                                        : false
                                                }
                                                minDate={
                                                    !shift.shift_id
                                                        ? dayjs().startOf("day")
                                                        : editedShiftsIds.includes(shift.shift_id as number)
                                                        ? dayjs().add(1, "day").startOf("day")
                                                        : undefined
                                                }
                                                label={t("mill_setup.shifts.effective_from")}
                                                format="MM/DD/YY"
                                                onChange={(newValue) => handleEffectiveAtChange(index, newValue)}
                                            />
                                        </FormControl>
                                        {touched.shifts &&
                                            errors &&
                                            errors.shifts &&
                                            (errors.shifts as FormikErrors<IShift>[])[index] &&
                                            (errors.shifts as FormikErrors<IShift>[])[index].shift_historic &&
                                            (errors.shifts as FormikErrors<IShift>[])[index].shift_historic
                                                ?.future_effective_at && (
                                                <FormControl error={true}>
                                                    <FormHelperText>
                                                        {t("field_errors.minimum_effective_date_error")}
                                                    </FormHelperText>
                                                </FormControl>
                                            )}

                                        <IconButton
                                            onClick={(event) => {
                                                handleShiftEditionInit(shift, index);
                                                event.stopPropagation();
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={(event) => {
                                                const isOnlyShiftProductionLines = productionLinesShifts.filter(
                                                    (plShifts) =>
                                                        plShifts.some(
                                                            (plShift) => plShift.shift.shift_id === shift.shift_id,
                                                        ) &&
                                                        plShifts.filter(
                                                            (plShift) =>
                                                                !values.shifts.some(
                                                                    (shiftValue) =>
                                                                        (shiftValue?.disabled_at ||
                                                                            shiftValue?.deleted_at) &&
                                                                        plShift.shift.shift_id === shiftValue?.shift_id,
                                                                ),
                                                        ).length === 1,
                                                );
                                                const isOnlyEffectiveShiftProductionLinesNewAssignment =
                                                    shift.production_line_shifts_changes.filter(
                                                        (plToBeDeletedShiftChanges) =>
                                                            plToBeDeletedShiftChanges.effective_at &&
                                                            values.shifts.filter((shiftValue) =>
                                                                shiftValue.production_line_shifts_changes.some(
                                                                    (plShiftChanges) =>
                                                                        plShiftChanges.production_line_id ===
                                                                            plToBeDeletedShiftChanges.production_line_id &&
                                                                        plShiftChanges.effective_at,
                                                                ),
                                                            ).length === 1,
                                                    );

                                                const productionLinesOptions = [
                                                    ...isOnlyShiftProductionLines.map((pl) => ({
                                                        id: pl[0].production_line.production_line_id,
                                                        name: pl[0].production_line.name,
                                                    })),
                                                    ...isOnlyEffectiveShiftProductionLinesNewAssignment.map((pl) => ({
                                                        id: pl.production_line_id,
                                                        name: pl.name,
                                                    })),
                                                ];

                                                if (productionLinesOptions.length > 0) {
                                                    handleShiftDeletionModal(event, index, shift, {
                                                        productionLinesOptions: productionLinesOptions,
                                                        shiftsOptions: values.shifts
                                                            .filter(
                                                                (shiftValue) =>
                                                                    !shiftValue?.disabled_at &&
                                                                    !shiftValue?.deleted_at &&
                                                                    (shift.shift_id
                                                                        ? shiftValue.shift_id !== shift.shift_id
                                                                        : shiftValue.name !== shift.name),
                                                            )
                                                            .map((shiftValue, optionsIndex) => {
                                                                const temporaryId = Number(`0.${optionsIndex + 1}`);
                                                                return {
                                                                    ...shiftValue,
                                                                    ...(!shiftValue?.shift_id && {
                                                                        shift_id: temporaryId,
                                                                    }),
                                                                    shift_historic: {
                                                                        ...shiftValue.shift_historic,
                                                                        ...(!shiftValue?.shift_id && {
                                                                            shift_id: temporaryId,
                                                                        }),
                                                                        daysDetails: dayArray.filter((dayDetail) =>
                                                                            shiftValue.shift_historic.days.includes(
                                                                                dayDetail.day_id,
                                                                            ),
                                                                        ),
                                                                    },
                                                                };
                                                            }),
                                                    });
                                                } else {
                                                    handleShiftDeletion(event, index, shift);
                                                }
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </div>
                                )}
                            </AccordionSummary>
                            <AccordionDetails className={classes.accordionDetails}>
                                <ToggleButtonGroup
                                    value={shift.shift_historic?.days}
                                    onChange={(_, newDays: number[]) => handleShiftDayChange(index, newDays)}
                                    aria-label="Select Days"
                                    className={classes.toggleButtonGroup}
                                >
                                    {dayArray.map((day) => {
                                        const defaultDayTranslatedName = translateDefaultEntity(
                                            i18n.language,
                                            day,
                                            "name",
                                            DefaultTranslationFieldsInTable.DAY,
                                        );
                                        return (
                                            <ToggleButton
                                                key={generateUniqueKey()}
                                                value={day.day_id}
                                                aria-label={defaultDayTranslatedName}
                                                className={classes.toggleButton}
                                            >
                                                {defaultDayTranslatedName[0]}
                                            </ToggleButton>
                                        );
                                    })}
                                    {touched.shifts &&
                                        errors &&
                                        errors.shifts?.length &&
                                        (errors.shifts as FormikErrors<IShift>[])[index] &&
                                        (errors.shifts as FormikErrors<IShift>[])[index].shift_historic &&
                                        (errors.shifts as FormikErrors<IShift>[])[index].shift_historic?.days && (
                                            <FormControl error={true}>
                                                <FormHelperText>
                                                    {t("field_errors.at_least_one_item_required")}
                                                </FormHelperText>
                                            </FormControl>
                                        )}
                                </ToggleButtonGroup>
                                <List className={classes.listContainer}>
                                    <ListItem className={classes.listItem}>
                                        <ListItemText
                                            className={classes.listTextTitle}
                                            primary={t("mill_setup.shifts.term")}
                                        />
                                        <div className={classes.timePickersWrapper}>
                                            <TimePicker
                                                label={t("mill_setup.shifts.start")}
                                                value={dayjs(shift.shift_historic?.start || "09:00:00", "HH:mm:ss")}
                                                onChange={(newValue) => handleTimeStartChange(newValue, index)}
                                                className={
                                                    conflictingTimes.find(
                                                        (conflictingTime) =>
                                                            conflictingTime.shift_id === shift.shift_id &&
                                                            conflictingTime.overlapping_time ===
                                                                shift.shift_historic.start,
                                                    )
                                                        ? classes.conflictingTime
                                                        : classes.nonConflictingTime
                                                }
                                            />
                                            <ListItemText className={classes.listText} primary="to" />
                                            <TimePicker
                                                label={t("mill_setup.shifts.end")}
                                                value={dayjs(shift.shift_historic?.end || "18:00:00", "HH:mm:ss")}
                                                onChange={(newValue) => handleTimeEndChange(newValue, index)}
                                                className={
                                                    conflictingTimes.find(
                                                        (conflictingTime) =>
                                                            conflictingTime.shift_id === shift.shift_id &&
                                                            conflictingTime.overlapping_time ===
                                                                shift.shift_historic.end,
                                                    )
                                                        ? classes.conflictingTime
                                                        : classes.nonConflictingTime
                                                }
                                            />
                                        </div>
                                    </ListItem>
                                </List>
                            </AccordionDetails>

                            {shiftsWithOverlappingErrorIds.includes(shift.shift_id as number) && (
                                <div key={generateUniqueKey()}>
                                    <Box className={classes.overlappingContainer}>
                                        <FormControl>
                                            <FormHelperText className={classes.overlappingText}>
                                                {t("mill_setup.shifts.shift_overlap")}
                                            </FormHelperText>
                                        </FormControl>
                                        <Box className={classes.shiftsChipsContainer}>
                                            {productionLineWithEditedShifts
                                                .filter((group) =>
                                                    group.some((item) => item.shift.shift_id === shift.shift_id),
                                                )
                                                .map((group) =>
                                                    group
                                                        .filter((item) => item.shift.shift_id !== shift.shift_id)

                                                        .map((item) => {
                                                            if (!uniqueShifts.includes(Number(item.shift.shift_id))) {
                                                                uniqueShifts.push(Number(item.shift.shift_id));

                                                                const editedShift = conflictingShifts
                                                                    .filter(
                                                                        (conflictingShift) =>
                                                                            Number(Object.keys(conflictingShift)) ===
                                                                            shift.shift_id,
                                                                    )
                                                                    .flatMap((item) =>
                                                                        Object.values(item).flatMap((idsArray) =>
                                                                            idsArray.filter(
                                                                                (id) =>
                                                                                    id !== (shift.shift_id as number),
                                                                            ),
                                                                        ),
                                                                    );

                                                                return (
                                                                    <Tooltip
                                                                        key={generateUniqueKey()}
                                                                        PopperProps={{ className: classes.tooltip }}
                                                                        title={
                                                                            <>
                                                                                <Typography
                                                                                    className={`${classes.tooltipTexts} ${classes.divisionLine}`}
                                                                                >
                                                                                    {t(
                                                                                        "corporate_backoffice.production_line_view.production_line_creation_form.category",
                                                                                    )}
                                                                                    :{" "}
                                                                                    <>
                                                                                        {productionLineWithEditedShifts
                                                                                            .filter((group) =>
                                                                                                group.some(
                                                                                                    (shift) =>
                                                                                                        shift.shift
                                                                                                            .shift_id ===
                                                                                                        item.shift
                                                                                                            .shift_id,
                                                                                                ),
                                                                                            )
                                                                                            .map((group, index) => {
                                                                                                const productionLinesUniqueIds: number[] =
                                                                                                    [];
                                                                                                return (
                                                                                                    <span
                                                                                                        key={index}
                                                                                                        className={
                                                                                                            classes.productionLinesList
                                                                                                        }
                                                                                                    >
                                                                                                        {group.map(
                                                                                                            (
                                                                                                                shift,
                                                                                                                shiftIndex,
                                                                                                            ) => {
                                                                                                                if (
                                                                                                                    !productionLinesUniqueIds.includes(
                                                                                                                        shift
                                                                                                                            .production_line
                                                                                                                            .production_line_id,
                                                                                                                    )
                                                                                                                ) {
                                                                                                                    productionLinesUniqueIds.push(
                                                                                                                        shift
                                                                                                                            .production_line
                                                                                                                            .production_line_id,
                                                                                                                    );
                                                                                                                    return (
                                                                                                                        <span
                                                                                                                            key={
                                                                                                                                shiftIndex
                                                                                                                            }
                                                                                                                        >
                                                                                                                            {`- ${shift.production_line.name}`}
                                                                                                                        </span>
                                                                                                                    );
                                                                                                                } else {
                                                                                                                    return undefined;
                                                                                                                }
                                                                                                            },
                                                                                                        )}
                                                                                                    </span>
                                                                                                );
                                                                                            })}
                                                                                    </>
                                                                                </Typography>
                                                                                <Typography
                                                                                    className={classes.tooltipTexts}
                                                                                >
                                                                                    {t(
                                                                                        "corporate_backoffice.production_line_view.production_line_creation_form.start",
                                                                                    )}
                                                                                    :{" "}
                                                                                    {
                                                                                        item.shift.shift_historic[0]
                                                                                            .start_time
                                                                                    }
                                                                                </Typography>
                                                                                <Typography
                                                                                    className={classes.tooltipTexts}
                                                                                >
                                                                                    {t(
                                                                                        "corporate_backoffice.production_line_view.production_line_creation_form.end",
                                                                                    )}
                                                                                    :{" "}
                                                                                    {
                                                                                        item.shift.shift_historic[0]
                                                                                            ?.end_time
                                                                                    }
                                                                                </Typography>
                                                                                <Typography
                                                                                    className={classes.tooltipTexts}
                                                                                >
                                                                                    {t(
                                                                                        "corporate_backoffice.production_line_view.production_line_creation_form.days",
                                                                                    )}
                                                                                    :{" "}
                                                                                    {item.shift.shift_historic[0]
                                                                                        .shift_days &&
                                                                                        item.shift.shift_historic[0]?.shift_days
                                                                                            .filter(
                                                                                                (shift_day) =>
                                                                                                    shift_day.is_enabled,
                                                                                            )
                                                                                            .map(
                                                                                                (shift_day) =>
                                                                                                    `${translateDefaultEntity(
                                                                                                        i18n.language,
                                                                                                        shift_day.day,
                                                                                                        "name",
                                                                                                        DefaultTranslationFieldsInTable.DAY,
                                                                                                    )} `,
                                                                                            )}
                                                                                </Typography>
                                                                            </>
                                                                        }
                                                                    >
                                                                        <Chip
                                                                            size="small"
                                                                            key={generateUniqueKey()}
                                                                            label={item.shift.name}
                                                                            className={
                                                                                editedShift.includes(
                                                                                    item.shift.shift_id as number,
                                                                                )
                                                                                    ? classes.overlappingShift
                                                                                    : classes.nonOverlappingShift
                                                                            }
                                                                        />
                                                                    </Tooltip>
                                                                );
                                                            } else {
                                                                return undefined;
                                                            }
                                                        }),
                                                )}
                                        </Box>
                                    </Box>
                                </div>
                            )}
                        </Accordion>
                    );
                })}
            </div>
            <ModalShiftDeletion
                open={openShiftDeletionModal}
                handleClose={handleCloseShiftDeletionModal}
                title={t("mill_setup.shifts.deletion_modal_title", { name: shiftDeletionData?.shift.name })}
            >
                <ShiftDeletionForm
                    valuesMillSettings={values}
                    shiftDeletionInfo={shiftDeletionData}
                    setFieldValueMillSettingsForm={setFieldValue}
                    handleClose={handleCloseShiftDeletionModal}
                />
            </ModalShiftDeletion>
        </>
    );
};

export default ShiftsInput;
